import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { appPropTypes, SCOPES } from '../../_shared';

import useVideo from '../hooks/useVideo';

const LibraryVideoContext = React.createContext();

const propTypes = {

	/**
	 * The video ID, if any.
	 */
	videoId: PropTypes.string,

	/**
	 * The type of scope for the video.
	 */
	scope: PropTypes.oneOf([
		SCOPES.SESSION,
		SCOPES.ORGANIZATION,
	]).isRequired,

	/**
	 * The scope ID for the video.
	 */
	scopeId: PropTypes.string.isRequired,

	/**
	 * The TechChange socket instance to handle real-time events.
	 */
	socket: appPropTypes.socket,

	/**
	 * The component children.
	 */
	children: PropTypes.node.isRequired,

};

/**
 * The library video provider.
 *
 * Fetches and manages state for a library video for a given video ID and scope.
 * Monitors real-time updates for "update:video" if a socket is provided.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object} The provider component.
 */
const LibraryVideoProvider = (props) => {

	const {
		videoId,
		scope,
		scopeId,
		socket,
		children,
	} = props;

	const {
		video,
		error,
		isLoading,
	} = useVideo(videoId, scope, scopeId, { socket });

	return (
		<LibraryVideoContext.Provider
			value={{
				video,
				videoError: error,
				isLoading,
			}}>
			{children}
		</LibraryVideoContext.Provider>
	);

};

LibraryVideoProvider.propTypes = propTypes;

/**
 * The library video context hook.
 *
 * @returns {Object} -             The shared context state.
 * @returns {Object} -.video       The latest video state.
 * @returns {Object} -.videoError  The the API error, if any, returned from the fetch request
 * @returns {Boolean} -.isLoading  True if the data is loading
 */
function useLibraryVideoContext() {

	const context = useContext(LibraryVideoContext);

	if (!context) {

		throw new Error('useLibraryVideoContext must be used within an LibraryVideoProvider');

	}

	return context;

}

export {
	LibraryVideoProvider as default,
	LibraryVideoProvider,
	useLibraryVideoContext,
};
